/**
 * MI ASISTENCIA en una cohorte (E3) — la pestaña «Asistencia» del expediente.
 *
 * Histórico propio (una fila por clase con pase de lista) y el porcentaje
 * enfrentado al criterio de certificación `academy.completion.attendance_pct`.
 *
 * Ni el porcentaje ni el objetivo se calculan aquí: el porcentaje lo deriva y
 * denormaliza el servidor (`use_cases::attendance_stats`) y el objetivo sale
 * del criterio que evalúa `/api/cohorts/:id/my-completion`. Esta pantalla los
 * enfrenta y los pinta.
 */
import { type Component, createResource, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import type { AttendanceStatusValue } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { fetchMyCompletion } from "../../completion/api";
import { useMyAttendanceRecord } from "../../hooks/use-student-record";

const STATUS_LABEL: Record<AttendanceStatusValue, MessageDescriptor> = {
  present: msg`Present`,
  late: msg`Late`,
  absent: msg`Absent`,
  excused: msg`Excused`,
};

const STATUS_CLASS: Record<AttendanceStatusValue, string> = {
  present: "bg-success/15 text-success",
  late: "bg-joy-sun/20 text-foreground",
  absent: "bg-danger/15 text-danger",
  excused: "bg-muted text-muted-foreground",
};

export const CohortAttendanceRecord: Component<{ cohortId: string; cohortName: string }> = (
  props,
) => {
  const { t } = useI18n();
  // Igual que `CompletionProgress`: un error (cohorte sin reglas, matrícula no
  // activa) simplemente deja el criterio fuera, no rompe la pestaña.
  const [completion] = createResource(
    () => props.cohortId,
    async (id) => fetchMyCompletion(id).catch(() => null),
  );
  const record = useMyAttendanceRecord(
    () => props.cohortId,
    () => completion()?.criteria ?? [],
  );

  const cert = record.certification;
  const { timeline } = record;

  return (
    <section class="space-y-3 rounded-2xl border border-border p-4" data-testid="attendance-record">
      <h3 class="font-display text-base font-bold">{props.cohortName}</h3>

      <Show
        when={cert().hasData}
        fallback={
          <p class="text-sm text-muted-foreground" data-testid="attendance-record-empty">
            {t(msg`No attendance has been recorded for you in this cohort yet.`)}
          </p>
        }
      >
        <div
          class="flex flex-wrap items-baseline gap-2 rounded-xl bg-muted/40 p-3"
          data-testid="attendance-rate"
          data-met={cert().met === null ? "" : String(cert().met)}
        >
          <span
            class={`font-display text-2xl font-extrabold ${
              cert().met === false ? "text-danger" : "text-foreground"
            }`}
          >
            {cert().ratePct ?? "—"}%
          </span>
          <Show
            when={cert().targetPct !== null}
            fallback={
              <span class="text-xs text-muted-foreground">
                {t(msg`This cohort has no attendance requirement.`)}
              </span>
            }
          >
            <span class="text-xs text-muted-foreground" data-testid="attendance-target">
              {t(msg`Certification requires at least`)} {cert().targetPct}%
            </span>
          </Show>
        </div>
      </Show>

      <Show when={timeline().length > 0}>
        <ul class="space-y-1" data-testid="attendance-timeline">
          <For each={timeline()}>
            {(row) => (
              <li
                class="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-1.5 text-sm"
                data-testid="attendance-timeline-row"
                data-status={row.status}
              >
                <span class="min-w-0">
                  <span class="font-semibold">{row.date}</span>{" "}
                  <span class="text-muted-foreground">{row.topic ?? ""}</span>
                </span>
                <span
                  class={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold ${STATUS_CLASS[row.status]}`}
                >
                  {t(STATUS_LABEL[row.status])}
                </span>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </section>
  );
};
